<template>
    <div class="dst search-page tw-overflow-hidden tw-min-h-screen">
        <div v-if="$scopedSlots.header" class="search-page__header">
            <div class="grid__container">
                <div class="grid__wrapper">
                    <div class="grid__item grid__item--gutter_m grid__item-12/12">
                        <!-- @slot Header content -->
                        <slot v-bind="response" name="header"></slot>
                    </div>
                </div>
            </div>
        </div>

        <loading-wrapper bg-color="grey-50" :loading="isBusy">
            <div class="lg:tw-container tw-mx-auto tw-px-6 tw-w-full">
                <div class="lg:tw-grid lg:tw-grid-cols-12 lg:tw-gap-4">
                    <div v-if="shouldShowSidebar" class="tw-hidden lg:tw-block tw-col-span-3">
                        <div>
                            <search-sidebar-filters
                                :filter-list="filterList"
                                @range-slider-update="onRangeSliderUpdate"
                                @checkbox-click="onCheckboxClick"
                                @clear-filters-click="onClearFiltersClick"
                                @items-toggler-click="onItemsTogglerClick"
                                @chip-click="onChipClick"
                                @promotion-switcher-change="onPromotionSwitcherChange"
                            />
                        </div>
                    </div>

                    <div
                        class="search-main"
                        :class="{
                            'tw-col-span-9': shouldShowSidebar,
                            'tw-col-span-12': !shouldShowSidebar,
                        }"
                    >
                        <div
                            v-if="response"
                            class="tw-flex tw-flex-col lg:tw-flex-row lg:tw-flex-wrap lg:tw-items-center"
                        >
                            <div class="tw-mb-4 lg:tw-order-1 lg:tw-ml-auto">
                                <div
                                    v-if="hasFilterItems"
                                    class="tw-grid lg:tw-block tw-grid-cols-2 tw-items-center tw-border-t tw-border-b tw-border-solid tw-border-border-color lg:tw-border-0"
                                >
                                    <button
                                        class="tw-p-3 lg:tw-hidden tw-text-lg tw-leading-sm tw-font-bold tw-flex tw-items-center tw-justify-center"
                                        @click="onSearchFiltersClick"
                                    >
                                        <span>
                                            {{ $t('search-page.button.filters') }}
                                        </span>
                                        <icon name="filters" size="xs" class="tw-ml-2" />
                                    </button>
                                    <template v-if="orderByList.length && hasItems">
                                        <div
                                            class="tw-flex tw-justify-center tw-items-center tw-relative before:tw-content-[''] before:tw-absolute before:tw-left-0 before:tw-h-1/2 before:tw-border-l before:tw-border-solid before:tw-border-border-color lg:before:tw-content-none"
                                        >
                                            <select
                                                class="no-style tw-p-3 tw-text-lg lg:tw-text-base tw-leading-sm lg:tw-leading-xs tw-font-bold lg:tw-font-normal tw-bg-transparent lg:tw-bg-white tw-w-full lg:tw-border lg:tw-border-solid lg:tw-border-border-color tw-text-center lg:tw-text-left lg:tw-p-3 tw-text-ellipsis tw-break-all"
                                                @change="onSelectChange"
                                            >
                                                <option
                                                    v-for="(orderBy, i) in orderByList"
                                                    :key="i"
                                                    :value="orderBy.href"
                                                    :selected="orderBy.state"
                                                >
                                                    {{ orderBy.name }}
                                                </option>
                                            </select>
                                        </div>
                                    </template>
                                </div>
                            </div>

                            <div class="tw-mb-6 lg:tw-w-full">
                                <!-- @slot Left side navbar filters -->
                                <slot v-bind="response" name="navbar-filters-left">
                                    <dk-chips
                                        v-if="response && response.suggestions"
                                        v-model="response.suggestions"
                                        variant="outlined"
                                        color="turquoise"
                                        class="tw-min-h-0 !tw-pb-0"
                                        @click="onChipClick"
                                    />
                                </slot>
                                <!-- @slot Right side navbar filters -->
                                <slot v-bind="response" name="navbar-filters-right"></slot>
                            </div>

                            <p v-if="!hasError && hasItems" class="tw-text-lg tw-leading-base tw-mb-4">
                                <template v-if="isPollBusy">
                                    <div class="icon-text">
                                        <icon name="spinner" size="xs" color="primary" spin />
                                        <span>{{ $t('search-page.message.loading') }}</span>
                                    </div>
                                </template>
                                <template v-else>
                                    {{ totalResultText }}
                                </template>
                            </p>
                        </div>

                        <slot name="pre-listing"></slot>

                        <div class="search-page__listing">
                            <div class="grid__wrapper">
                                <template v-if="hasError">
                                    <p class="grid__item ts--body">
                                        {{ error.message }}
                                    </p>
                                </template>
                                <template v-else>
                                    <template v-if="hasItems">
                                        <search-listing
                                            :items="response.items"
                                            :item-wrapper-attrs="{
                                                class: {
                                                    'm-bottom--m': true,
                                                    'grid__item-4/12--large': !isDriveSparkOffersPage,
                                                    'grid__item-12/12': isDriveSparkOffersPage,
                                                },
                                            }"
                                        >
                                            <template #before>
                                                <!-- @slot Content before search listing -->
                                                <slot
                                                    v-bind="{ count: response.items.length }"
                                                    name="interstitial"
                                                ></slot>
                                            </template>

                                            <template #item="{ item }">
                                                <component
                                                    :is="cardComponent"
                                                    target="_blank"
                                                    v-bind="getCardComponentProps(item)"
                                                    @click-select-promotion="
                                                        (submodelId, offerId) => onSelectPromotion(submodelId, offerId)
                                                    "
                                                />
                                            </template>
                                        </search-listing>
                                    </template>
                                    <template v-else>
                                        <p class="grid__item ts--body color--grey-500 ts-align--center">
                                            {{ $t('search-page.message.no-results') }}
                                        </p>
                                    </template>
                                </template>
                            </div>
                            <div v-if="!hasError && hasItems" class="ts-align--center">
                                <dk-pagination
                                    :total-entities="currentItemCount"
                                    :per-page="(response.pagination && response.pagination.results_per_page) || 12"
                                    :page="(response.pagination && response.pagination.current_page) || 1"
                                    class="ts-align--center d--inline-flex m-top--m"
                                    @paging-clicked="onPagingClicked"
                                />
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </loading-wrapper>
    </div>
</template>

<script>
import queryString from 'query-string';
import isEmpty from 'lodash/isEmpty';
import throttle from 'lodash/throttle';
import { API_RESOURCE_OFFERS, API_RESOURCE_SEARCH, API_RESOURCE_DRIVESPARK_OFFERS } from '@v4/search/config';
import SearchSidebarFilters from '@v4/search/components/organisms/SearchSidebarFilters.vue';
import DkBadge from '@v4/common/components/atoms/DkBadge.vue';
import LoadingWrapper from '@v4/common/components/atoms/LoadingWrapper.vue';
import OfferCard from '@v4/submodel-offers/components/molecules/OfferCard.vue';
import DkScore from '@v4/common/components/molecules/DkScore.vue';
import CardPromotion from '@v4/common/components/molecules/CardPromotion.vue';
import { dkModalAware } from '@v4/common/components/mixins/modal';
import apiErrorHandler from '@v4/common/components/mixins/api-error-handler';
import { getApiRootUrl } from '@v4/config';
import { doRequest } from '@v4/http/request';
import { getSearchResults } from '@v4/services/api/v3/search';
import {
    windowHistoryReplaceStateParams,
    windowHistoryPushStateParams,
    getQueryParams,
    stringifyQueryParams,
    stringifyUrl,
} from '@v4/common/utils/url';
import { pushImpressions, pushPromoView } from '@v4/common/utils/data-layer';
import dataLayerHandler from '@v4/common/components/mixins/data-layer-handler';
import { on, supportsPassiveEvents } from '@v4/common/utils/dom';
import gaEventHandler from '@v4/common/components/mixins/google-analytics';
import DkChips from '@v4/common/components/molecules/DkChips.vue';
import { EventBus } from '@v4/common/utils/event-bus';
import SearchListing from '@v4/search/components/organisms/SearchListing';
import Icon from '@v4/common/components/atoms/Icon';

export default {
    name: 'SearchPage',
    components: {
        SearchSidebarFilters,
        LoadingWrapper,
        OfferCard,
        CardPromotion,
        DkPagination: () => ({
            component: import(
                /* webpackChunkName: "DkPagination-vue" */ '@v4/common/components/atoms/DkPagination.vue'
            ),
        }),
        DkSearchVehicleCard: () => ({
            component: import(
                /* webpackChunkName: "DkSearchVehicleCard-vue" */ '@v4/common/components/organisms/DkSearchVehicleCard.vue'
            ),
        }),
        PromoBanner: () => ({
            component: import(
                /* webpackChunkName: "PromoBanner-vue" */ '@v4/common/components/molecules/PromoBanner/PromoBanner.vue'
            ),
        }),
        DkScore,
        DkBadge,
        DkChips,
        SearchListing,
        Icon,
    },
    mixins: [dkModalAware, apiErrorHandler, dataLayerHandler, gaEventHandler],
    props: {
        apiResource: {
            type: String,
            default: '',
        },
        data: {
            type: Object,
            required: true,
        },
    },
    data() {
        return {
            isBusy: false,
            isPollBusy: false,
            sidebarVisible: true,
            fetchedData: {},
            requestCount: 0,
            inactivityCount: 0,
            inactivityTimeoutId: 0,
            maxRequestCount: 10,
            pollingTimeoutId: null,
            additionalQueryParams: {
                // eslint-disable-next-line camelcase
                expanded_lists: getQueryParams().expanded_lists || '',
            },
        };
    },
    computed: {
        currentItemCount() {
            if (!this.response || !this.response.pagination || !this.response.pagination.number_results) {
                return 0;
            }

            return this.response.pagination.number_results;
        },
        response() {
            return isEmpty(this.fetchedData) ? this.data : this.fetchedData;
        },
        /**
         * Cards components are dynamically loaded, in base
         * of the apiResource value.
         */
        cardComponent() {
            switch (this.theApiResource) {
                case API_RESOURCE_OFFERS:
                    return 'offer-card';
                case API_RESOURCE_SEARCH:
                    return 'card-promotion';
            }

            return 'dk-search-vehicle-card';
        },
        theApiResource() {
            return this.apiResource === API_RESOURCE_DRIVESPARK_OFFERS
                ? API_RESOURCE_DRIVESPARK_OFFERS
                : this.apiResource;
        },
        isDriveSparkOffersPage() {
            return this.apiResource.includes(API_RESOURCE_DRIVESPARK_OFFERS);
        },
        isOffersPage() {
            return this.apiResource === API_RESOURCE_OFFERS;
        },
        isSearchPage() {
            return this.apiResource === API_RESOURCE_SEARCH;
        },
        totalResultText() {
            return this.$tc(
                this.isOffersPage ? 'search-page.models-found' : 'search-page.cars-found',
                this.currentItemCount,
                {
                    n: this.isSearchPage ? this.response.pagination.total_items : this.currentItemCount,
                }
            );
        },
        filterList() {
            return (this.response && this.response.facetList) || [];
        },
        hasFilterItems() {
            return !!this.filterList.length;
        },
        shouldShowSidebar() {
            return this.sidebarVisible && this.hasFilterItems;
        },
        shouldPoolData() {
            return this.fetchedData.completed === false;
        },
        hasItems() {
            return this.response && this.response.items && !!this.response.items.length;
        },
        orderByList() {
            return (this.response && this.response.orderByList) || [];
        },
    },
    mounted() {
        /**
         * On DriveSparK we preserve back navigation:
         * 1. Listen to 'popstate' event.
         * 2. Replace current state with itself but with preloaded data.
         */
        on(window, 'popstate', this.onPopState);
        window.history.replaceState(this.data, document.title, window.location.href);

        if (this.isDriveSparkOffersPage && this.completed === false) {
            const stringifiedQueryParams = stringifyQueryParams(getQueryParams());
            const currentResourceUrl = `${this.theApiResource}${stringifiedQueryParams}`;
            this.fetchData(currentResourceUrl);
        }

        if (this.$listeners.inactivity) {
            this.handleInactivity();
        }

        if (this.data && this.data.events && this.data.events['view_item_list']) {
            this.pushGtagEvent('view_item_list', null, this.data.events);
        }
    },
    methods: {
        clearAndSetIncativityTimeout() {
            if (this.inactivityCount >= 1) {
                return;
            }

            clearTimeout(this.inactivityTimeoutId);
            this.inactivityTimeoutId = setTimeout(() => {
                this.inactivityCount += 1;
                this.$emit('inactivity');
            }, 60000);
        },
        handleInactivity() {
            on(
                document,
                'click',
                this.clearAndSetIncativityTimeout,
                supportsPassiveEvents() ? { passive: true } : false
            );
            on(window, 'scroll', throttle(this.clearAndSetIncativityTimeout, 60));
            on(window, 'mousemove', this.clearAndSetIncativityTimeout);

            this.clearAndSetIncativityTimeout();
        },
        onPopState(e) {
            if (this.isDriveSparkOffersPage && e && e.state) {
                if ('popup' in e.state) {
                    // Handle a special case in which the history state
                    // is overwritten by the Popup Maker's Exit Intent plugin,
                    // and, when navigating with the back button to the initial
                    // `/offerte` page (`page 1`), the resulting page shows no results.
                    // In this case, restore the initial data received from the backend.
                    this.fetchedData = this.data;
                } else {
                    // Use state as previous fetchedData
                    this.fetchedData = e.state;
                }

                /**
                 * If previous state hadn't finish loading data, we request new one.
                 */
                if (e.state.completed === false) {
                    const stringifiedQueryParams = stringifyQueryParams(getQueryParams());
                    const currentResourceUrl = `${this.theApiResource}${stringifiedQueryParams}`;
                    this.fetchData(currentResourceUrl);
                }
            }
        },
        getCardComponentProps(item) {
            if (this.isDriveSparkOffersPage) {
                return { item, isBusy: this.isPollBusy, gaEvents: this.data.events };
            }

            return { item, gaEvents: this.data.events };
        },
        onPagingClicked(page) {
            // this.pushGaEvent('Search page', 'pagination change', `click on number ${page} page`);
            if (window.ga) {
                window.ga('send', 'event', 'Search page', 'pagination change', `click on number ${page} page`);
            }

            this.pushGtagEvent('click_change_page', null, this.data.events, {
                // eslint-disable-next-line camelcase
                type_pagination: page,
            });

            this.pushGtagEvent('view_item_list', null, this.data.events);

            this.updateBrowserQueryStringParamAndFetch('pag', page)
                .then(({ analytics }) => {
                    if (analytics) {
                        if (analytics.promotions) {
                            pushPromoView(analytics.promotions);
                        } else if (analytics.impressions) {
                            pushImpressions(analytics.impressions);
                        }
                    }
                })
                .catch(() => {
                    // @TODO Understand what to do in this case: we received a timeout
                });
        },
        onCardClick(vehicle) {
            const analytics = this.response.analytics;

            if (!analytics) {
                return;
            }

            if (analytics.promotions) {
                this.pushPromotionClickToDataLayer(vehicle);
            } else if (analytics.impressions) {
                this.pushProductClickToDataLayer(vehicle, analytics.actionField);
            }
        },
        onRangeSliderUpdate(slider, { id }) {
            const min = slider[2][0];
            const max = slider[2][1];

            this.pushGaEvent('Search page', 'slider filter change', `${id} filter`);

            return this.updateBrowserQueryStringParamAndFetch(id, `${min},${max}`);
        },
        onPromotionSwitcherChange(payload) {
            const { href } = payload;

            this.pushGaEvent('Search page', 'promotion filter click', 'Promotion switcher filter');

            return this.fetchData(href);
        },
        onCheckboxClick(e) {
            const url = e.target.value;

            this.pushGaEvent('Search page', 'checkbox filter click', `${e.target.name} filter`);

            return this.fetchData(url);
        },
        onSelectChange(e) {
            const { value: url, children } = e.target;
            const selected = [...children].find((item) => item.value === url);

            this.pushGaEvent('Search page', 'sorting select change', `${selected.text} filter`);

            return this.fetchData(url);
        },
        onChipClick({ item }) {
            this.pushGaEvent('Search page', 'suggestion list filters click', `${item.name} filter`);
            return this.fetchData(item.href);
        },
        onClearFiltersClick() {
            this.pushGaEvent('Search page', 'filter click', 'clear all filters');
            // Let's keep `sort` query parameter if exists
            return this.fetchData(`${this.theApiResource}${stringifyQueryParams({ sort: getQueryParams().sort })}`);
        },
        onSearchFiltersClick() {
            this.pushGaEvent('Search page', 'filter click', 'click to open filters');
            this.sidebarVisible = false;

            return import(
                /* webpackChunkName: "SearchFiltersModal-vue" */ '@v4/search/components/organisms/SearchFiltersModal/SearchFiltersModal.vue'
            ).then(({ default: SearchFiltersModal }) => {
                this.showModal(
                    SearchFiltersModal,
                    {
                        filters: this.filterList,
                        handleRangeSliderUpdate: this.onRangeSliderUpdate,
                        handlePromotionSwitcherChange: this.onPromotionSwitcherChange,
                        handleCheckboxClick: this.onCheckboxClick,
                        handleClearFiltersClick: this.onClearFiltersClick,
                        handleItemsTogglerClick: this.onItemsTogglerClick,
                        handleChipClick: this.onChipClick,
                    },
                    {
                        name: 'search-filters-modal',
                        height: 'auto',
                        classes: 'modal--fullscreen_large-x',
                    },
                    {
                        closed: this.showSidebar,
                    }
                ).then(this.showSidebar);
            });
        },
        onItemsTogglerClick({ filter, expanded }) {
            const filterId = (filter && filter.id) || null;
            const expandedLists = this.additionalQueryParams.expanded_lists;
            const separator = '|';

            let expandedListsItems = expandedLists && expandedLists.length ? expandedLists.split(separator) : [];

            if (!expanded) {
                expandedListsItems = expandedListsItems.filter((id) => id !== filterId);
            } else if (!expandedListsItems.includes(filterId)) {
                expandedListsItems.push(filterId);
            }

            this.$set(this.additionalQueryParams, 'expanded_lists', expandedListsItems.join(separator));
        },
        showSidebar() {
            this.sidebarVisible = true;
        },
        updateBrowserQueryStringParamAndFetch(key, value) {
            const parsedQueryParams = getQueryParams();

            // @TODO cristian.frumusanu@autoxy.it
            // "dirty" hotfix for the "+" char in URL.
            // Normally the "+" char is used as a space in the URL
            // and when parsed back, it results as a space and must
            // be replaced with an actual "+" to be sent to the API.
            // When Andrea returns from Peru we'll try to introduce
            // a label—name/key system to deal with this properly.
            if (parsedQueryParams.seatsGrouped && parsedQueryParams.seatsGrouped === '6 ') {
                parsedQueryParams.seatsGrouped = '6+';
            }

            // Whenever we would like to fetch new data we should ensure
            // that `pag` parameter it's not sent to the request.
            if (key !== 'pag') {
                Reflect.deleteProperty(parsedQueryParams, 'pag');
            }

            const parsedApiUrl = queryString.parseUrl(this.response.url || '') || {};
            const { query = {} } = parsedApiUrl;
            const params = {
                ...query,
                ...parsedQueryParams,
            };

            const stringifiedQueryParams = stringifyQueryParams({ ...params, [key]: value });

            const href = `${this.theApiResource}${stringifiedQueryParams}`;

            return this.fetchData(href);
        },
        scrollTop() {
            document.body.scrollIntoView({ behavior: 'smooth' });
        },
        getRequestUrlByHref(href) {
            let url = '';
            let query = {};

            if (href) {
                const parsedUrl = queryString.parseUrl(href);

                query = { ...parsedUrl.query, ...this.additionalQueryParams };

                // Remove empty query params
                Object.keys(this.additionalQueryParams).forEach((key) => {
                    if (Object.prototype.hasOwnProperty.call(query, key) && !query[key]) {
                        Reflect.deleteProperty(query, key);
                    }
                });

                url = stringifyUrl({ url: `${getApiRootUrl()}${parsedUrl.url}`, query });
            }

            return { url, query };
        },
        fetchData(href) {
            if (!href) {
                return;
            }

            if (this.isDriveSparkOffersPage) {
                return this.pollData(href, true);
            }

            this.isBusy = true;

            const { query, url } = this.getRequestUrlByHref(href);

            // Update browser's query string
            windowHistoryReplaceStateParams(query);

            return doRequest(url)
                .then((response) => response.json())
                .then((data) => {
                    this.resetDataError();
                    this.fetchedData = data;

                    return data;
                })
                .catch(() => {
                    this.setMessageError();
                    this.response.items = [];
                })
                .finally(() => {
                    this.isBusy = false;
                    this.scrollTop();
                });
        },
        continuePollData(href, delay) {
            this.pollingTimeoutId = setTimeout(() => this.pollData(href), delay);
        },
        pollData(href, firstTry = false) {
            if (firstTry) {
                /**
                 * Clear polling timeout in order to avoid a useless request already queued.
                 * Set `completed` to false in order to let `shouldPollData` be true.
                 * Reset requestCount to zero.
                 * Scroll top.
                 */
                clearTimeout(this.pollingTimeoutId);
                this.fetchedData.completed = false;
                this.requestCount = 0;
                this.scrollTop();
            }

            if (!this.shouldPoolData || this.requestCount > this.maxRequestCount) {
                this.isPollBusy = false;
                return;
            }

            this.isPollBusy = true;
            this.isBusy = this.requestCount === 0; // Overlay only for first request.
            this.requestCount += 1;

            const delay = 500 * this.requestCount;
            const { query, url } = this.getRequestUrlByHref(href);

            return getSearchResults(url)
                .then(({ data }) => {
                    EventBus.$emit('SearchPage:fetchDataSuccess', data);
                    this.notifyLazySizes();
                    this.resetDataError();

                    this.fetchedData = data;

                    this.$nextTick(() => {
                        // continuing fetching data until `completed` property has value `true`
                        if (this.shouldPoolData) {
                            return this.continuePollData(href, delay);
                        }
                    });

                    this.isPollBusy = false;

                    windowHistoryPushStateParams(this.fetchedData, query); // Update browser's query string

                    return data;
                })
                .catch((error) => {
                    EventBus.$emit('SearchPage:fetchDataError', error);

                    if (error.response) {
                        // the request was made and the server responded with a status code
                        // the falls out of the range of `2xx`
                        this.setMessageError();
                    } else if (error.error && error.error.event && error.error.event.type === 'timeout') {
                        // the request was aborted because of the timeout
                        return this.continuePollData(href, delay);
                    }

                    this.isPollBusy = false;

                    return error;
                })
                .finally(() => {
                    this.isBusy = false;
                });
        },
        notifyLazySizes() {
            if (window.lazySizes && window.lazySizes.notifyUpdate) {
                window.lazySizes.notifyUpdate('.ds-vehicle-card__img');
            }
        },
        onSelectPromotion(submodelId, offerId) {
            this.pushGtagEvent('select_promotion', `${submodelId}_${offerId}`, this.data.events);
        },
    },
};
</script>
